import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import ContractTermsV2 from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/ContractTermsV2";
import Files from "@/pages/deals/components/commercials/DealCommercialsV2/Files/Files";
import ContractFinancialRecognition from "./financialRecognition/ContractFinancialRecognition";
import { useDeals } from "@/pages/deals/hooks";
import { ContractData, ContractTab } from "@/pages/contracts/types";
import {
  FINANCIALS_WRITE_PERMISSION,
  useHasPermission
} from "@/services/auth/AuthContext";
import { type ContractFieldUpdate } from "./ContractDetailedView";
import UpdateTermsAction from "@/pages/contracts/components/updateTerms/UpdateTermsAction";
import { TermsHistory } from "@/pages/contracts/components/termsHistory/TermsHistory";
import { PendingAmendmentBadge } from "@/pages/contracts/components/termsHistory/PendingAmendmentBadge";
import { useTermsVersions } from "@/pages/contracts/components/termsHistory/hooks";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { ProposalsSection } from "@/pages/contracts/components/proposals/ProposalsSection";
import { TAB_SEARCH_PARAM } from "@/components/layouts/DetailedView";

interface Props {
  contract: ContractData;
  onUpdateField: ContractFieldUpdate;
}

const ContractTermsAndServicesTab: React.FC<Props> = ({
  contract,
  onUpdateField
}) => {
  const { t } = useTranslation();
  const canEditFinancials = useHasPermission(FINANCIALS_WRITE_PERMISSION);
  const financialsLocked = !canEditFinancials;

  const { entities: deals } = useDeals(
    contract.dealId ? [contract.dealId] : [],
    { enabled: !!contract.dealId }
  );
  const productId = deals[0]?.productId ?? undefined;

  // The badge and the timeline read the same version list, on the same query
  // key, so TanStack serves both from one request.
  const { enabled: amendmentsEnabled, loading: amendmentsLoading } =
    useFeatureFlag(FeatureFlags.CRM_9208_CONTRACT_AMENDMENTS);
  const { data: versionsData } = useTermsVersions(contract.id, {
    enabled: !amendmentsLoading && amendmentsEnabled
  });
  // Services change through Update terms when it is offered. True while the
  // flag loads too, so add and edit never flash in and out.
  const servicesViaUpdateTerms = amendmentsLoading || amendmentsEnabled;

  return (
    // A grid rather than two flex columns, because the stacked order and the
    // side-by-side order differ: below `lg-mid` the sections read terms →
    // financial recognition + files → history, and at `lg-mid` the history
    // moves back under the terms in the left column with the sidebar beside
    // both. Explicit placement reorders them without moving any of it in the
    // DOM, which nesting one column inside another could not do.
    // Row gap is the sidebar's own separation when stacked; at `lg-mid` it is
    // the terms-to-history gap. The design puts both at 40px, so the single
    // `gap-10` carries it and no breakpoint overrides the row axis.
    <div className="w-full max-w-[1800px] mx-auto grid grid-cols-1 gap-10 lg-mid:grid-cols-[minmax(0,1fr)_clamp(280px,calc(31.25vw_-_220px),380px)]">
      <div className="min-w-0 flex flex-col gap-4 lg-mid:col-start-1 lg-mid:row-start-1">
        <div className="flex items-center gap-3 flex-wrap">
          <Text variant="smSubtitle">
            {t("deals.commercials.contractTermsTitle")}
          </Text>
          {/* Gate the render, not just the fetch: `enabled: false` stops the
              request but TanStack still serves anything already in the cache
              under this key, which the timeline shares. */}
          {!amendmentsLoading && amendmentsEnabled ? (
            <PendingAmendmentBadge versions={versionsData?.versions} />
          ) : null}
        </div>
        <ContractTermsV2
          contractId={contract.id}
          productId={contract.productId ?? productId}
          dealId={contract.dealId ?? undefined}
          confirmServiceChanges
          financialsLocked={financialsLocked}
          headerAction={<UpdateTermsAction contractId={contract.id} />}
          hideServiceRowActions={servicesViaUpdateTerms}
          hideAddService={servicesViaUpdateTerms}
          groupBundles
        />
        {/* A proposal belongs to the contract's deal, and is generated from the
            deal's live contract; a duplicate or frozen copy would show another
            contract's proposals. */}
        {contract.dealId &&
        !contract.duplicatedFromContractId &&
        !contract.snapshotOfDealId ? (
          <ProposalsSection
            className="mt-6"
            dealId={contract.dealId}
            contractId={contract.id}
            returnTo={`/contracts/${contract.id}?${TAB_SEARCH_PARAM}=${ContractTab.TERMS}`}
          />
        ) : null}
      </div>
      {/* Second in the DOM, so a stacked layout reads it before the history —
          the sidebar carries the figures somebody scrolling a narrow screen is
          looking for, and the history is long. At `lg-mid` it moves to the
          right column and spans both rows. */}
      <div className="min-w-0 flex flex-row gap-10 lg-mid:flex-col lg-mid:col-start-2 lg-mid:row-start-1 lg-mid:row-span-2">
        <div className="flex-1 lg-mid:flex-none flex flex-col gap-6 min-w-0">
          <ContractFinancialRecognition
            contract={contract}
            onUpdateField={onUpdateField}
            editDisabled={financialsLocked}
            editDisabledTooltipText={t("common.noEditPermission")}
          />
        </div>
        <div className="flex-1 lg-mid:flex-none flex flex-col gap-4 min-w-0">
          <Text variant="smSubtitle">{t("deals.commercials.filesTitle")}</Text>
          <Files
            attachments={contract.uploadFiles?.contracts ?? []}
            onAttachmentsChange={(attachments) =>
              onUpdateField("uploadFiles", { contracts: attachments })
            }
            disabled={financialsLocked}
            disabledTooltipText={t("common.noEditPermission")}
          />
        </div>
      </div>
      {/* Rendered as a grid item rather than wrapped in one: it returns null
          when the flag is off or the contract has no versions, and an empty
          wrapper would leave its row's gap behind. */}
      <TermsHistory
        contractId={contract.id}
        className="min-w-0 lg-mid:col-start-1 lg-mid:row-start-2"
      />
    </div>
  );
};

export default ContractTermsAndServicesTab;
